import {announce} from '@github-ui/aria-live'
import {DragAndDrop, type OnDropArgs} from '@github-ui/drag-and-drop'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {type ScribeArtifact, useDeleteScribeArtifact, useReorderScribeArtifact} from '@github-ui/scribe-artifacts'
import type {PanelArtifact} from '@github-ui/scribe-artifacts/ArtifactPanelContent'
import {useArtifactsPanelActions, useArtifactsPanelState} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import {isStaff} from '@github-ui/stats'
import {ChevronDownIcon, ChevronRightIcon, FileIcon, LinkIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, CounterLabel, IconButton, Label, Pagination} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {TEST_IDS} from '../../constants/test-ids'
import {PersistedArtifactRow, SortablePersistedArtifactRow} from './ArtifactRow'
import {ArtifactsBodyPortalProvider} from './ArtifactsBodyPortalProvider'
import styles from './ArtifactsSection.module.css'
import {CreateArtifactDialog} from './CreateArtifactDialog'
import {CreateLinkDialog} from './CreateLinkDialog'
import {shouldShowArtifactsSection} from './issue-artifacts-visibility'
import {computeArtifactReorder} from './reorder-artifacts'
import type {IssueArtifact} from './types'
import {useCreateArtifactDialog} from './use-create-artifact-dialog'

const ARTIFACTS_STAFF_FEEDBACK_URL = 'https://gh.io/scribe-artifacts-feedback'
const ARTIFACTS_PRIVATE_PREVIEW_FEEDBACK_URL = 'https://gh.io/issue-artifacts-private-preview'

export function mapArtifactType(
  type: string,
  name: string,
  isDevelopment = process.env.NODE_ENV === 'development',
): IssueArtifact['type'] {
  const htmlArtifactsEnabled = isFeatureEnabled('scribe_artifacts_html')
  if (htmlArtifactsEnabled && type === 'embedded_html') return 'html'
  if (htmlArtifactsEnabled && isDevelopment && type === 'generic' && name.toLowerCase().endsWith('.html')) return 'html'
  return type === 'plan' || type === 'link' ? type : 'generic'
}

/** Maps a Scribe API artifact (snake_case payload) onto the section's view model. */
function mapScribeArtifact(artifact: ScribeArtifact, index: number): IssueArtifact {
  const numericId = typeof artifact.id === 'number' ? artifact.id : Number(artifact.id)
  return {
    id: Number.isFinite(numericId) ? numericId : index + 1,
    number: artifact.number ?? index + 1,
    type: mapArtifactType(artifact.type, artifact.name),
    name: artifact.name,
    body: artifact.body ?? '',
    bodyHTML: artifact.body_html ?? undefined,
    viewscreenUrl: artifact.viewscreen_url,
    actor: {login: artifact.creator?.login ?? '', avatarUrl: ''},
    createdAt: artifact.created_at ?? '',
    updatedAt: artifact.updated_at ?? '',
    hasEdits: artifact.has_edits,
    updatedByActor: artifact.updated_by_actor ? {login: artifact.updated_by_actor.login} : undefined,
  }
}

function mapScribePanelArtifact(artifact: ScribeArtifact, index: number, resourceId: number): PanelArtifact {
  const mappedArtifact = mapScribeArtifact(artifact, index)
  return {
    id: `${mappedArtifact.type}:${mappedArtifact.id}`,
    number: mappedArtifact.number,
    type: mappedArtifact.type,
    name: mappedArtifact.name,
    body: mappedArtifact.body,
    bodyHTML: mappedArtifact.bodyHTML ?? '',
    trustedBodyHTML: artifact.body_html ?? undefined,
    createdAt: mappedArtifact.createdAt,
    updatedAt: mappedArtifact.updatedAt,
    hasEdits: mappedArtifact.hasEdits,
    updatedByActor: mappedArtifact.updatedByActor,
    resourceType: 'issue',
    resourceId,
    viewscreenUrl: mappedArtifact.type === 'html' ? mappedArtifact.viewscreenUrl : undefined,
  }
}

function orderArtifactsByPosition(artifacts: ScribeArtifact[]): ScribeArtifact[] {
  const positionedArtifacts = artifacts.filter(
    (artifact): artifact is ScribeArtifact & {position: number} => artifact.position !== undefined,
  )
  if (positionedArtifacts.length !== artifacts.length) return artifacts

  return positionedArtifacts
    .map((artifact, index) => ({artifact, index}))
    .sort((left, right) => left.artifact.position - right.artifact.position || left.index - right.index)
    .map(({artifact}) => artifact)
}

type ArtifactsSectionContentProps = {
  resourceId: number
  repositoryId?: number
  organizationId?: number
  artifacts: ScribeArtifact[]
  onSelectArtifact: (artifact: PanelArtifact) => void
  canReorder: boolean
  onArtifactDelete?: (id: number) => void
  onLastArtifactDeleted?: () => void
}

function ArtifactsSectionContent({
  resourceId,
  repositoryId,
  organizationId,
  artifacts,
  onSelectArtifact,
  canReorder,
  onArtifactDelete,
  onLastArtifactDeleted,
}: ArtifactsSectionContentProps) {
  const {editingArtifact} = useArtifactsPanelState()
  const {closeEditArtifact, editArtifact, updateArtifact} = useArtifactsPanelActions()
  const persistedArtifacts = useMemo<IssueArtifact[]>(() => artifacts.map(mapScribeArtifact), [artifacts])
  const [optimisticOrder, setOptimisticOrder] = useState<number[] | null>(null)
  const displayedArtifacts = useMemo(() => {
    if (!optimisticOrder) return persistedArtifacts

    const artifactsByNumber = new Map(persistedArtifacts.map(artifact => [artifact.number, artifact]))
    if (
      optimisticOrder.length !== persistedArtifacts.length ||
      optimisticOrder.some(number => !artifactsByNumber.has(number))
    ) {
      return persistedArtifacts
    }

    return optimisticOrder.flatMap(number => {
      const artifact = artifactsByNumber.get(number)
      return artifact ? [artifact] : []
    })
  }, [optimisticOrder, persistedArtifacts])

  const {mutate: deleteArtifact} = useDeleteScribeArtifact({resourceType: 'issue', resourceId})
  const {mutate: reorderArtifact, isPending: reorderPending} = useReorderScribeArtifact({
    resourceType: 'issue',
    resourceId,
  })
  const [reorderError, setReorderError] = useState<string | null>(null)
  const reorderInFlightRef = useRef(false)

  const artifactNumbersById = useMemo(
    () => new Map(persistedArtifacts.map(artifact => [artifact.id, artifact.number])),
    [persistedArtifacts],
  )
  const trustedBodyHTMLByNumber = useMemo(
    () => new Map(artifacts.map((artifact, index) => [artifact.number ?? index + 1, artifact.body_html ?? undefined])),
    [artifacts],
  )

  const totalArtifacts = persistedArtifacts.length

  const handleDelete = useCallback(
    (id: number) => {
      const number = artifactNumbersById.get(id)
      if (number === undefined) return
      deleteArtifact(
        {number},
        {
          onSuccess: () => {
            onArtifactDelete?.(id)
            if (totalArtifacts === 1) {
              onLastArtifactDeleted?.()
            }
          },
        },
      )
    },
    [artifactNumbersById, deleteArtifact, onArtifactDelete, onLastArtifactDeleted, totalArtifacts],
  )

  const handleArtifactUpdated = useCallback(
    (artifact: ScribeArtifact) => {
      const updatedArtifact = mapScribeArtifact(artifact, 0)
      updateArtifact({
        id: `${updatedArtifact.type}:${updatedArtifact.id}`,
        number: updatedArtifact.number,
        type: updatedArtifact.type,
        name: updatedArtifact.name,
        body: updatedArtifact.body,
        bodyHTML: updatedArtifact.bodyHTML ?? '',
        trustedBodyHTML: artifact.body_html ?? undefined,
        createdAt: updatedArtifact.createdAt,
        updatedAt: updatedArtifact.updatedAt,
        hasEdits: updatedArtifact.hasEdits,
        updatedByActor: updatedArtifact.updatedByActor,
        resourceType: 'issue',
        resourceId,
        viewscreenUrl: updatedArtifact.type === 'html' ? updatedArtifact.viewscreenUrl : undefined,
      })
    },
    [resourceId, updateArtifact],
  )

  const handleDrop = useCallback(
    ({dragMetadata, dropMetadata, isBefore}: OnDropArgs<number>) => {
      if (reorderInFlightRef.current) return
      const reorder = computeArtifactReorder(displayedArtifacts, dragMetadata.id, dropMetadata.id, isBefore)
      if (!reorder) return

      const nextOptimisticOrder = displayedArtifacts.map(artifact => artifact.number)
      const [movedArtifactNumber] = nextOptimisticOrder.splice(reorder.fromIndex, 1)
      if (movedArtifactNumber === undefined) return
      nextOptimisticOrder.splice(reorder.toIndex, 0, movedArtifactNumber)

      reorderInFlightRef.current = true
      setOptimisticOrder(nextOptimisticOrder)
      setReorderError(null)
      reorderArtifact(
        {number: dragMetadata.id, afterNumber: reorder.afterNumber},
        {
          onSuccess: () => {
            sendEvent('issue_artifact.reorder', {
              artifact_number: dragMetadata.id,
              after_number: reorder.afterNumber ?? null,
              from_position: reorder.fromIndex + 1,
              to_position: reorder.toIndex + 1,
              issue_id: resourceId,
              repository_id: repositoryId,
              organization_id: organizationId,
              platform: 'dotcom',
            })
          },
          onError: () => {
            const message = 'Unable to reorder artifact. Previous order restored.'
            setReorderError(message)
            announce(message, {assertive: true})
          },
          onSettled: () => {
            reorderInFlightRef.current = false
            setOptimisticOrder(null)
          },
        },
      )
    },
    [displayedArtifacts, organizationId, reorderArtifact, repositoryId, resourceId],
  )

  if (totalArtifacts === 0) {
    return null
  }

  return (
    <>
      {reorderError ? (
        <Banner className={styles.Error} variant="critical">
          <Banner.Title>{reorderError}</Banner.Title>
        </Banner>
      ) : null}
      {canReorder ? (
        <DragAndDrop
          aria-label="Artifacts"
          className={styles.List}
          enableTouch
          items={displayedArtifacts.map(artifact => ({id: artifact.number, title: artifact.name}))}
          onDrop={handleDrop}
          renderOverlay={(item, index) => {
            const artifact = displayedArtifacts.find(candidate => candidate.number === item.id)
            return artifact ? (
              <SortablePersistedArtifactRow
                key={`${artifact.type}:${artifact.id}`}
                {...artifact}
                index={index}
                isDragOverlay
                resourceId={resourceId}
                viewscreenUrl={artifact.type === 'html' ? artifact.viewscreenUrl : undefined}
                repositoryId={repositoryId}
                organizationId={organizationId}
              />
            ) : null
          }}
        >
          {displayedArtifacts.map((artifact, index) => (
            <SortablePersistedArtifactRow
              key={`${artifact.type}:${artifact.id}`}
              {...artifact}
              index={index}
              trustedBodyHTML={trustedBodyHTMLByNumber.get(artifact.number)}
              resourceId={resourceId}
              viewscreenUrl={artifact.type === 'html' ? artifact.viewscreenUrl : undefined}
              repositoryId={repositoryId}
              organizationId={organizationId}
              onSelect={onSelectArtifact}
              onDelete={handleDelete}
              onEdit={editArtifact}
              reorderPending={reorderPending}
            />
          ))}
        </DragAndDrop>
      ) : (
        <ul className={styles.List} aria-label="Artifacts">
          {displayedArtifacts.map(artifact => (
            <PersistedArtifactRow
              key={`${artifact.type}:${artifact.id}`}
              {...artifact}
              trustedBodyHTML={trustedBodyHTMLByNumber.get(artifact.number)}
              resourceId={resourceId}
              viewscreenUrl={artifact.type === 'html' ? artifact.viewscreenUrl : undefined}
              repositoryId={repositoryId}
              organizationId={organizationId}
              onSelect={onSelectArtifact}
              onDelete={handleDelete}
              onEdit={editArtifact}
            />
          ))}
        </ul>
      )}
      {editingArtifact &&
        (editingArtifact.artifact.type === 'link' ? (
          <CreateLinkDialog
            resourceId={resourceId}
            artifact={editingArtifact.artifact}
            onClose={closeEditArtifact}
            onUpdated={handleArtifactUpdated}
            returnFocusRef={editingArtifact.returnFocusRef}
          />
        ) : (
          <CreateArtifactDialog
            resourceId={resourceId}
            artifact={editingArtifact.artifact}
            onClose={closeEditArtifact}
            onUpdated={handleArtifactUpdated}
            returnFocusRef={editingArtifact.returnFocusRef}
          />
        ))}
    </>
  )
}

type ArtifactsSectionProps = {
  resourceId: number
  repositoryId?: number
  organizationId?: number
  artifacts: ScribeArtifact[]
  canReorder?: boolean
  currentPage?: number
  hasNextPage?: boolean
  focusOnMount?: boolean
  /** True when no section (e.g. sub-issues) follows this card, so it meets the timeline flush. */
  isLastSection?: boolean
  onArtifactDelete?: (id: number) => void
  onFocusTargetReady?: () => void
  onLastArtifactDeleted?: () => void
  onPageChange?: (page: number) => void
}

export function ArtifactsSection({
  resourceId,
  repositoryId,
  organizationId,
  artifacts,
  canReorder = false,
  currentPage = 1,
  hasNextPage = false,
  focusOnMount = false,
  isLastSection = true,
  onArtifactDelete,
  onFocusTargetReady,
  onLastArtifactDeleted,
  onPageChange,
}: ArtifactsSectionProps) {
  const [isExpanded, setIsExpanded] = useState<boolean | null>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)
  const viewerIsStaff = isStaff()
  const feedbackLabel = viewerIsStaff ? 'Staff' : 'Private preview'

  const {selectArtifact: handleSelectArtifact, removeArtifact, reconcileArtifacts} = useArtifactsPanelActions()

  const artifactCount = artifacts.length
  const pageCount = currentPage + (hasNextPage ? 1 : 0)
  const panelArtifacts = useMemo(
    () => artifacts.map((artifact, index) => mapScribePanelArtifact(artifact, index, resourceId)),
    [artifacts, resourceId],
  )
  // Default expanded when artifacts exist, collapsed when empty. User toggle wins.
  const effectiveExpanded = isExpanded ?? (artifactCount > 0 || currentPage > 1)

  useEffect(() => {
    reconcileArtifacts(panelArtifacts)
  }, [panelArtifacts, reconcileArtifacts])

  useEffect(() => {
    if (!focusOnMount) return
    const toggleButton = toggleButtonRef.current
    if (!toggleButton) return
    toggleButton.focus()
    onFocusTargetReady?.()
  }, [focusOnMount, onFocusTargetReady])

  const toggleExpanded = useCallback(() => {
    setIsExpanded(expanded => !(expanded ?? artifactCount > 0))
  }, [artifactCount])

  const handleArtifactCreated = useCallback(() => {
    setIsExpanded(true)
  }, [])

  const handleArtifactDelete = useCallback(
    (id: number) => {
      const artifact = artifacts.map(mapScribeArtifact).find(item => item.id === id)
      if (artifact) {
        removeArtifact(`${artifact.type}:${artifact.id}`)
      }
      onArtifactDelete?.(id)
    },
    [artifacts, onArtifactDelete, removeArtifact],
  )

  const {
    createArtifactDialog,
    openCreateArtifactDialog: handleCreateArtifact,
    openCreateLinkDialog: handleCreateLink,
    triggerRef: addArtifactButtonRef,
  } = useCreateArtifactDialog({resourceId, onCreated: handleArtifactCreated})

  if (!shouldShowArtifactsSection(artifacts, currentPage)) return null

  return (
    <ArtifactsBodyPortalProvider>
      <div
        className={clsx(
          styles.Container,
          isFeatureEnabled('issue_inline_avatars') && styles.inlineAvatars,
          isLastSection && styles.lastSection,
        )}
        data-testid={TEST_IDS.issueArtifactsSection}
      >
        <div className={styles.Header}>
          <div className={styles.TitleGroup}>
            <IconButton
              ref={toggleButtonRef}
              className={styles.ToggleButton}
              icon={effectiveExpanded ? ChevronDownIcon : ChevronRightIcon}
              variant="invisible"
              size="small"
              aria-label={effectiveExpanded ? 'Collapse artifacts' : 'Expand artifacts'}
              aria-expanded={effectiveExpanded}
              onClick={toggleExpanded}
            />
            <span className={styles.TitleLabel}>Artifacts</span>
            {artifactCount > 0 && pageCount === 1 ? <CounterLabel>{artifactCount}</CounterLabel> : null}
            <a
              href={viewerIsStaff ? ARTIFACTS_STAFF_FEEDBACK_URL : ARTIFACTS_PRIVATE_PREVIEW_FEEDBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${feedbackLabel} artifacts feedback. Opens in a new tab.`}
              className={styles.StaffFeedbackLink}
            >
              <Label variant={viewerIsStaff ? 'accent' : 'secondary'}>{feedbackLabel}</Label>
            </a>
          </div>
          <ActionMenu anchorRef={addArtifactButtonRef}>
            <ActionMenu.Anchor>
              <IconButton
                icon={PlusIcon}
                variant="invisible"
                size="small"
                aria-label="Add artifact"
                disabled={resourceId <= 0}
              />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                <ActionList.Item onSelect={handleCreateLink}>
                  <ActionList.LeadingVisual>
                    <LinkIcon />
                  </ActionList.LeadingVisual>
                  Add link
                </ActionList.Item>
                <ActionList.Item onSelect={handleCreateArtifact}>
                  <ActionList.LeadingVisual>
                    <FileIcon />
                  </ActionList.LeadingVisual>
                  Create artifact
                </ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>

        <div
          className={clsx(styles.Body, !effectiveExpanded && styles.collapsed)}
          inert={!effectiveExpanded || undefined}
        >
          <div className={styles.BodyInner}>
            <ErrorBoundary fallback={null} critical={false} boundaryName="ArtifactsSection">
              <ArtifactsSectionContent
                resourceId={resourceId}
                repositoryId={repositoryId}
                organizationId={organizationId}
                artifacts={currentPage === 1 && !hasNextPage ? orderArtifactsByPosition(artifacts) : artifacts}
                canReorder={canReorder && currentPage === 1 && !hasNextPage}
                onSelectArtifact={handleSelectArtifact}
                onArtifactDelete={handleArtifactDelete}
                onLastArtifactDeleted={onLastArtifactDeleted}
              />
            </ErrorBoundary>
            {pageCount > 1 && onPageChange ? (
              <Pagination
                className={styles.Pagination}
                currentPage={currentPage}
                pageCount={pageCount}
                showPages={false}
                onPageChange={(event, page) => {
                  event.preventDefault()
                  onPageChange(page)
                }}
              />
            ) : null}
          </div>
        </div>

        {createArtifactDialog}
      </div>
    </ArtifactsBodyPortalProvider>
  )
}
